import React, { useState, useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";

import processing from "./images/processing.png";
import crushing from "./images/crushing.png";
import milling from "./images/milling.png";
import chemicals from "./images/chemicals.png";

function ServicePage({
  title = "Mining & Mineral Processing",
  subtitle = "Comprehensive equipment and services for the mining and mineral processing industry.",
  equipment = [],
  services = [],
}) {

    // ✅ Set the page title (browser tab)
  useEffect(() => {
    document.title = "Services | Prosep Services";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = subtitle;

    return () => {
      document.title = "Prosep Services";
    };
  }, []);

  const equipmentList = equipment.length
    ? equipment
    : [
        {
          title: "Mineral Processing",
          image: processing,
        },
        {
          title: "Crushing",
          image: crushing,
        },
        {
          title: "Milling",
          image: milling,
        },
        {
          title: "Chemicals for Mining",
          image: chemicals,
        },
      ];

  const serviceList = services.length
    ? services
    : [
        "Sales & Maintenance",
        "Installation & Commissioning",
        "Gravity Testing: Lab and Pilot Testing",
        "Gravity Audit",
      ];

  return (
    <>
      <Header />

      <main className="service-page">
        {/* ============ HERO ============ */}
        <section className="service-hero">
          <div className="service-hero-inner">
            <h1 className="service-hero-title">
              {title}
              <span className="service-hero-accent">.</span>
            </h1>
            <p className="service-hero-subtitle">{subtitle}</p>
          </div>
        </section>

        {/* ============ AVAILABLE EQUIPMENT ============ */}
        <section className="service-section">
          <div className="service-section-inner">
            <div className="service-section-head service-section-head-left">
              <h2 className="service-section-title service-section-title-left">
                Available <span className="service-section-accent">Equipment</span>
              </h2>
            </div>

            <div className="equipment-grid">
              {equipmentList.map((item, index) => (
                <div
                  className="equipment-card"
                  key={index}
                  style={{ backgroundImage: `url(${item.image})` }}
                >
                  <div className="equipment-overlay">
                    <span className="equipment-num">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="equipment-caption">
                      <span className="equipment-line"></span>
                      <h3 className="equipment-title">{item.title}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ SERVICES WE OFFER ============ */}
        <section className="service-section service-section-alt">
          <div className="service-section-inner">
            <div className="service-section-head">
              <h2 className="service-section-title">Services We Offer</h2>
              <p className="service-section-desc">
                We offer a wide range of mining and general industry services
                to meet your needs.
              </p>
            </div>

            <div className="service-grid">
              {serviceList.map((item, index) => (
                <div className="service-card" key={index}>
                  <span className="service-card-icon">✓</span>
                  <p className="service-card-text">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <style>{`
        .service-page {
          width: 100%;
          background: #ffffff;
          font-family: Arial, sans-serif;
        }

        /* ============ HERO ============ */
        .service-hero {
          width: 100%;
          padding: 110px 40px 90px;
          background: linear-gradient(
            135deg,
            #001a3d 0%,
            #002a5c 50%,
            #0047a3 100%
          );
          text-align: center;
          box-sizing: border-box;
        }
        .service-hero-inner {
          max-width: 1000px;
          margin: 0 auto;
        }
        .service-hero-title {
          font-size: 52px;
          font-weight: 900;
          color: #ffffff;
          margin: 0 0 20px;
          letter-spacing: 0.5px;
          line-height: 1.15;
        }
        .service-hero-accent {
          color: #b8965a;
        }
        .service-hero-subtitle {
          font-size: 19px;
          line-height: 1.6;
          color: #dbe5f0;
          margin: 0;
          max-width: 720px;
          margin-left: auto;
          margin-right: auto;
        }

        /* ============ COMMON SECTION ============ */
        .service-section {
          width: 100%;
          padding: 90px 40px;
          box-sizing: border-box;
        }
        .service-section-alt {
          background: #f7f9fc;
        }
        .service-section-inner {
          max-width: 1400px;
          margin: 0 auto;
        }

        .service-section-head {
          text-align: center;
          margin-bottom: 56px;
        }
        .service-section-head-left {
          text-align: left;
        }
        .service-section-title {
          font-size: 36px;
          font-weight: 900;
          color: #111;
          margin: 0;
          letter-spacing: 0.3px;
          position: relative;
          display: inline-block;
          padding-bottom: 16px;
        }
        .service-section-title::after {
          content: "";
          position: absolute;
          left: 50%;
          bottom: 0;
          transform: translateX(-50%);
          width: 52px;
          height: 3px;
          background: #c8102e;
        }
        .service-section-title-left {
          padding-bottom: 0;
        }
        .service-section-title-left::after {
          display: none;
        }
        .service-section-accent {
          color: #b8965a;
        }
        .service-section-desc {
          font-size: 17px;
          line-height: 1.7;
          color: #4b5563;
          margin: 24px auto 0;
          max-width: 700px;
        }

        /* ============ EQUIPMENT GRID (image cards) ============ */
        .equipment-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .equipment-card {
          position: relative;
          width: 100%;
          aspect-ratio: 16 / 10;
          border-radius: 12px;
          overflow: hidden;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          cursor: pointer;
          transition: transform 0.35s ease, box-shadow 0.35s ease;
        }
        .equipment-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
        }

        .equipment-overlay {
          position: absolute;
          inset: 0;
          padding: 28px 32px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.55) 0%,
            rgba(0, 0, 0, 0.15) 40%,
            rgba(0, 0, 0, 0.35) 65%,
            rgba(0, 0, 0, 0.75) 100%
          );
          transition: background 0.35s ease;
        }
        .equipment-card:hover .equipment-overlay {
          background: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.6) 0%,
            rgba(0, 0, 0, 0.2) 40%,
            rgba(0, 0, 0, 0.45) 65%,
            rgba(0, 0, 0, 0.85) 100%
          );
        }

        .equipment-num {
          font-size: 34px;
          font-weight: 900;
          color: #ffffff;
          line-height: 1;
          letter-spacing: -0.5px;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
        }

        .equipment-caption {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .equipment-line {
          display: block;
          width: 100%;
          height: 1px;
          background: rgba(255, 255, 255, 0.85);
        }
        .equipment-title {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
          letter-spacing: 1.5px;
          color: #ffffff;
          text-transform: uppercase;
          line-height: 1.2;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
        }

        /* ============ SERVICES GRID ============ */
        .service-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          max-width: 1000px;
          margin: 0 auto;
        }
        .service-card {
          display: flex;
          align-items: center;
          gap: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 22px 26px;
          transition: all 0.25s ease;
        }
        .service-card:hover {
          transform: translateX(4px);
          border-color: #c8102e;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
        }
        .service-card-icon {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #fef2f3;
          color: #c8102e;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 900;
        }
        .service-card-text {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #111;
          line-height: 1.5;
        }

        /* ============ RESPONSIVE ============ */
        @media (max-width: 900px) {
          .service-hero {
            padding: 80px 24px 70px;
          }
          .service-hero-title {
            font-size: 38px;
          }
          .service-hero-subtitle {
            font-size: 17px;
          }

          .service-section {
            padding: 70px 24px;
          }
          .service-section-title {
            font-size: 28px;
          }
          .service-section-desc {
            font-size: 16px;
          }

          .equipment-grid {
            grid-template-columns: 1fr;
            gap: 18px;
          }
          .equipment-overlay {
            padding: 22px 24px;
          }
          .equipment-num {
            font-size: 28px;
          }
          .equipment-title {
            font-size: 20px;
          }

          .service-grid {
            grid-template-columns: 1fr;
            gap: 14px;
          }
          .service-card {
            padding: 18px 20px;
          }
        }

        @media (max-width: 500px) {
          .service-hero {
            padding: 60px 20px 50px;
          }
          .service-hero-title {
            font-size: 30px;
          }
          .service-hero-subtitle {
            font-size: 15px;
          }
          .service-section {
            padding: 50px 20px;
          }

          .equipment-card {
            aspect-ratio: 4 / 3;
          }
          .equipment-overlay {
            padding: 18px 18px;
          }
          .equipment-num {
            font-size: 24px;
          }
          .equipment-title {
            font-size: 16px;
            letter-spacing: 1px;
          }

          .service-card-icon {
            width: 32px;
            height: 32px;
            font-size: 14px;
          }
          .service-card-text {
            font-size: 15px;
          }
        }
      `}</style>
    </>
  );
}

export default ServicePage;